import { AlertTriangle, CheckCircle2, FileSearch, Play, ShieldAlert } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import type { LedgerClassificationScheme, LedgerProvisionRunResult } from '@/api/ledger-types'
import { session } from '@/api/session'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { ProblemAlert } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { branchLabel } from '@/lib/branch'
import { formatBusinessDate, formatShortDate } from '@/lib/datetime'

import { LedgerAuditTrail } from './audit-trail-panel'
import { BranchReachNotice, BranchSelect } from './branch-reach'
import { formatOverdueBand, formatProvisionRate } from './constants'
import { useRunProvisionClassification } from './mutations'
import { LEDGER_PERMISSIONS, useLedgerPermissions } from './permissions'
import { useBranchBusinessDay, useClassificationSchemes, useLedgerBranchReach } from './queries'

const today = () => new Date().toISOString().slice(0, 10)

/**
 * Provisioning — the one posting a ledger permission holder can make without a countersignature.
 *
 * `POST /loans/classification-runs` is gated on `ledger.provision.run` despite its route living
 * under `/loans`: it classifies a branch's live rounds against the scheme in force as at a date and
 * posts the movement in the loan-loss allowance into the general ledger. A manual journal is the
 * other way a ledger holder puts an entry into the book by hand, and that one is countersigned —
 * this executes on the click, which is why the whole screen is arranged around saying what it is
 * about to do before it does it.
 *
 * Four facts shape everything below.
 *
 * **It posts the movement, not the level.** The allowance already carried is what the new
 * requirement is measured against, so a second run on an unchanged book posts nothing and answers
 * with a null entry number. That makes the run safe to repeat and makes "nothing posted" an
 * ordinary outcome rather than a failure.
 *
 * **It is not countersigned.** Every other consequential ledger operation in the catalogue is.
 * This one executes on the click, which is why it goes through a confirmation that names the
 * branch, the date and the scheme first.
 *
 * **The journal is dated today, valued as at the date chosen.** The posting carries today's
 * business date, so the branch needs *today* open and accepting postings — not the day of `asOf`.
 * Its value date is `asOf`, and because the posting is system-generated it is accepted by a
 * soft-closed period, which a manual journal never is.
 *
 * **With no scheme in force nothing is classified and nothing is posted.** The count of rounds
 * that went unjudged is reported beside it, because a run that judged nothing must not read as a
 * book with nothing wrong.
 *
 * The whole screen refuses below `lg`. It is a posting into the general ledger read against a
 * rates table and a business-day state — the case `docs/mobile-policy.md` puts in the refuse
 * column rather than a layout to squeeze.
 */
export function ProvisionRunSection() {
  const permissions = useLedgerPermissions()
  const reach = useLedgerBranchReach()

  // The operator's own choice, and nothing until they make one. The header's branch is applied as
  // a default below rather than seeded into state, because on the first render it is frequently
  // not settled yet — and lazily initialised state would hold that first value for the life of the
  // screen.
  const [chosenBranchId, setChosenBranchId] = useState<string | null>(null)
  const [asOf, setAsOf] = useState(today)
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState<LedgerProvisionRunResult | null>(null)
  const [showJournal, setShowJournal] = useState(false)

  /*
   * Only ever a branch the server confirmed this user may act in. The header's stored branch can
   * be stale — left by an earlier session, or revoked by an access review — and a run posts a
   * journal into the general ledger, so an identifier this screen cannot name is not one it will
   * post against.
   */
  const branchId = chosenBranchId ?? session.getBranchId() ?? reach.homeBranchId
  const branch = reach.branches.find((candidate) => candidate.id === branchId) ?? null

  const schemes = useClassificationSchemes(permissions.canReadSchemes)
  const day = useBranchBusinessDay(branch?.id ?? '')
  const run = useRunProvisionClassification()

  /**
   * The scheme the server will resolve for this date: the latest one whose effective date is not
   * after it. Computed here only to show the rates before the run — the server resolves its own,
   * and its answer, not this, is what the posting is made against.
   */
  const schemeInForce = useMemo(
    () => resolveScheme(schemes.data ?? [], asOf),
    [schemes.data, asOf],
  )

  if (!permissions.canRunProvision) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
        <ShieldAlert className="size-6 text-warning-text" aria-hidden />
        <p className="text-sm font-medium text-heading">Provisioning is restricted</p>
        <p className="max-w-lg text-xs text-muted">
          Running classification posts a journal into the general ledger, and it needs{' '}
          <span className="font-mono">{LEDGER_PERMISSIONS.provisionRun}</span>. Your role does not
          hold it.
        </p>
      </div>
    )
  }

  const submittable = branch !== null && asOf !== ''

  return (
    <RequiresWiderScreen
      screen="Provisioning"
      reason="It posts the loan-loss movement into the general ledger, and the decision is only sound with the scheme's rates, the branch's trading day and the run's own totals visible together."
    >
      <div className="space-y-4">
        <BranchReachNotice reach={reach} />

        <SchemePanel
          scheme={schemeInForce}
          asOf={asOf}
          canRead={permissions.canReadSchemes}
          isPending={schemes.isPending && permissions.canReadSchemes}
          isEmpty={(schemes.data ?? []).length === 0}
        />

        <Card>
          <CardHeader>
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2">
                <Play className="size-4 text-brand-text" aria-hidden />
                Run classification and provisioning
              </CardTitle>
              <p className="mt-0.5 text-xs text-muted">
                Not countersigned. Safe to repeat — the movement is measured against the allowance
                already carried.
              </p>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <BranchSelect
                id="provision-branch"
                label="Branch"
                branches={reach.branches}
                value={branch?.id ?? null}
                onChange={(next) => {
                  setChosenBranchId(next)
                  setResult(null)
                  setShowJournal(false)
                  run.reset()
                }}
                description="Rounds are classified one branch at a time."
              />

              <div className="space-y-1.5">
                <Label htmlFor="provision-as-of">As at</Label>
                <Input
                  id="provision-as-of"
                  type="date"
                  value={asOf}
                  max={today()}
                  onChange={(event) => {
                    setAsOf(event.target.value)
                    setResult(null)
                    setShowJournal(false)
                    run.reset()
                  }}
                  aria-describedby="provision-as-of-help"
                />
                <p id="provision-as-of-help" className="text-xs text-muted">
                  The date arrears are measured at, and the journal&apos;s value date. It also picks
                  the scheme: the one in force on this date, not today&apos;s.
                </p>
              </div>
            </div>

            <PostingReadiness
              branchLabelText={branch === null ? null : branchLabel(branch)}
              isPending={branch !== null && day.isPending}
              businessDate={day.data?.businessDate ?? null}
              acceptsPostings={day.data?.acceptsPostings ?? false}
            />

            {run.isError ? <ProblemAlert error={run.error} /> : null}

            {result ? (
              <RunResult
                result={result}
                showJournal={showJournal}
                onToggleJournal={() => setShowJournal((current) => !current)}
                canReadAudit={permissions.canReadAudit}
              />
            ) : null}

            <div>
              <Button
                variant="primary"
                disabled={!submittable || run.isPending}
                onClick={() => setConfirming(true)}
              >
                {run.isPending ? 'Running…' : 'Run provisioning'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Post the provision movement?"
          description="This classifies the branch's live rounds and posts the movement in the loan-loss allowance straight into the general ledger. It is not countersigned, and this application has no screen that reverses a journal."
          confirmLabel="Run and post"
          isPending={run.isPending}
          error={run.isError ? run.error : null}
          onConfirm={() => {
            if (branch === null) {
              return
            }

            run.mutate(
              // String identifier in the application, `long` on the endpoint. Converted once, here,
              // from the branch this screen actually named in the confirmation above.
              { branchId: Number(branch.id), asOf },
              {
                onSuccess: (answer) => {
                  setResult(answer)
                  setShowJournal(false)
                  setConfirming(false)
                },
              },
            )
          }}
        >
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Branch</dt>
              <dd className="text-heading">{branch === null ? '—' : branchLabel(branch)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">As at</dt>
              <dd className="numeric text-heading tabular-nums">
                {asOf === '' ? '—' : formatBusinessDate(asOf)}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted">Scheme in force</dt>
              <dd className="text-right text-heading">
                {!permissions.canReadSchemes
                  ? 'Not readable with your permissions'
                  : schemeInForce === null
                    ? 'None — nothing will be classified'
                    : `${schemeInForce.code}${schemeInForce.isProvisional ? ' (provisional)' : ''}`}
              </dd>
            </div>
          </dl>
        </ConfirmDialog>
      </div>
    </RequiresWiderScreen>
  )
}

/** The latest scheme effective on or before the date, which is how the server resolves it. */
function resolveScheme(
  schemes: LedgerClassificationScheme[],
  asOf: string,
): LedgerClassificationScheme | null {
  let best: LedgerClassificationScheme | null = null

  for (const scheme of schemes) {
    if (scheme.effectiveFrom <= asOf && (best === null || scheme.effectiveFrom > best.effectiveFrom)) {
      best = scheme
    }
  }

  return best
}

/**
 * The rates a run will apply, and — where they cannot be read — the fact that they cannot.
 *
 * An empty rates table and a refused read look identical on screen unless one of them says so.
 * The distinction matters here more than most places: "no scheme is in force" means every round
 * goes unclassified and no provision is posted at all, which is a finding, not a blank.
 */
function SchemePanel({
  scheme,
  asOf,
  canRead,
  isPending,
  isEmpty,
}: {
  scheme: LedgerClassificationScheme | null
  asOf: string
  canRead: boolean
  isPending: boolean
  isEmpty: boolean
}) {
  if (!canRead) {
    return (
      <Alert tone="warning">
        <ShieldAlert aria-hidden />
        <div className="space-y-1">
          <AlertTitle>The rates behind this run cannot be shown to you</AlertTitle>
          <AlertDescription>
            The classification schemes are served by the loans module and gated on{' '}
            <span className="font-mono text-xs">{LEDGER_PERMISSIONS.loansView}</span>, which your
            role does not hold. The run itself will still resolve and apply the scheme in force —
            you simply cannot read the rates it used before pressing the button.
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  if (isPending) {
    return <Skeleton className="h-40 w-full" />
  }

  if (scheme === null) {
    return (
      <Alert tone="warning">
        <AlertTriangle aria-hidden />
        <div className="space-y-1">
          <AlertTitle>
            {isEmpty
              ? 'No classification scheme has ever been put into force'
              : `No scheme was in force on ${formatShortDate(asOf)}`}
          </AlertTitle>
          <AlertDescription>
            A run against this date will classify nothing and post nothing. It will still answer
            successfully and report every live round as unclassified — which is a finding about the
            configuration, not a clean book.
          </AlertDescription>
        </div>
      </Alert>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0 space-y-1">
          <CardTitle className="flex flex-wrap items-center gap-2">
            {scheme.name}
            <span className="font-mono text-xs text-muted">{scheme.code}</span>
            {scheme.isProvisional ? <Badge tone="warning">Provisional</Badge> : null}
          </CardTitle>
          <p className="text-xs text-muted">
            In force from{' '}
            <span className="numeric tabular-nums">{formatShortDate(scheme.effectiveFrom)}</span> ·
            provision measured on{' '}
            {scheme.provisionBasis === 'PrincipalOutstanding'
              ? 'principal outstanding'
              : 'principal and interest outstanding'}
            {scheme.sourceReference === null ? '' : ` · ${scheme.sourceReference}`}
          </p>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {scheme.isProvisional ? (
          <Alert tone="warning">
            <AlertTriangle aria-hidden />
            <div className="space-y-1">
              <AlertTitle>These rates are a working assumption</AlertTitle>
              <AlertDescription>
                The scheme is marked provisional, so the figures have not been confirmed against the
                regulatory instrument. The provision it produces is real and is posted all the same.
              </AlertDescription>
            </div>
          </Alert>
        ) : null}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Class</TableHead>
              <TableHead>Days overdue</TableHead>
              <TableHead className="text-right">Provision</TableHead>
              <TableHead>Interest</TableHead>
              <TableHead>Performing</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {scheme.bands.map((band) => (
              <TableRow key={band.classCode}>
                <TableCell>
                  <span className="font-medium text-heading">{band.className}</span>{' '}
                  <span className="font-mono text-2xs text-muted">{band.classCode}</span>
                </TableCell>
                <TableCell className="numeric tabular-nums">
                  {formatOverdueBand(band.minDaysOverdue, band.maxDaysOverdue)}
                </TableCell>
                <TableCell className="numeric text-right tabular-nums">
                  {formatProvisionRate(band.provisionRate)}
                </TableCell>
                <TableCell>{band.suspendsInterestAccrual ? 'Suspended' : 'Accrues'}</TableCell>
                <TableCell>{band.isPerforming ? 'Yes' : 'No'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {scheme.restructuredFloorClassCode === null ? null : (
          <p className="text-xs text-muted">
            A restructured facility cannot be classified better than{' '}
            <span className="font-mono">{scheme.restructuredFloorClassCode}</span>
            {scheme.restructuredCoolingDays === null
              ? '.'
              : ` until it has performed for ${scheme.restructuredCoolingDays} days.`}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Whether the branch can actually take the posting.
 *
 * The journal carries **today's** business date rather than the date being provisioned, so the
 * check that matters is today's day at that branch. The nuance is worth stating: a run that finds
 * no movement posts nothing and succeeds regardless, so this is a warning about what would happen
 * if there is something to post — not a reason to refuse the run.
 */
function PostingReadiness({
  branchLabelText,
  isPending,
  businessDate,
  acceptsPostings,
}: {
  branchLabelText: string | null
  isPending: boolean
  businessDate: string | null
  acceptsPostings: boolean
}) {
  if (branchLabelText === null) {
    return null
  }

  if (isPending) {
    return <Skeleton className="h-16 w-full" />
  }

  if (acceptsPostings && businessDate !== null) {
    return (
      <p className="text-xs text-muted">
        {branchLabelText} is trading on{' '}
        <span className="numeric tabular-nums">{formatBusinessDate(businessDate)}</span>, which is
        the business date the journal will carry. Its value date is the date you are provisioning as
        at.
      </p>
    )
  }

  return (
    <Alert tone="warning">
      <AlertTriangle aria-hidden />
      <div className="space-y-1">
        <AlertTitle>{branchLabelText} is not accepting postings</AlertTitle>
        <AlertDescription>
          The provision journal carries today&apos;s business date, so a movement cannot be posted
          until today&apos;s day is open at this branch. A run that finds no movement will still
          succeed and post nothing.
        </AlertDescription>
      </div>
    </Alert>
  )
}

/** What the run actually did, including the case where the honest answer is "nothing". */
function RunResult({
  result,
  showJournal,
  onToggleJournal,
  canReadAudit,
}: {
  result: LedgerProvisionRunResult
  showJournal: boolean
  onToggleJournal: () => void
  canReadAudit: boolean
}) {
  return (
    <div className="space-y-3">
      {!result.schemeConfigured ? (
        <Alert tone="warning">
          <AlertTriangle aria-hidden />
          <div className="space-y-1">
            <AlertTitle>Nothing was classified</AlertTitle>
            <AlertDescription>
              No scheme was in force on{' '}
              <span className="numeric tabular-nums">{formatShortDate(result.asOf)}</span>, so every
              live round went unjudged and no provision was posted. This is a configuration finding.
            </AlertDescription>
          </div>
        </Alert>
      ) : result.schemeIsProvisional ? (
        <Alert tone="warning">
          <AlertTriangle aria-hidden />
          <div className="space-y-1">
            <AlertTitle>Provisional rates were applied</AlertTitle>
            <AlertDescription>
              The scheme in force is marked provisional. The posting is real; the rates behind it
              are not final.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <div className="grid gap-4 rounded-card border border-border-subtle bg-surface-muted p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Figure label="Classified" value={<span className="numeric tabular-nums">{result.classified}</span>} />
        <Figure
          label="Unclassified"
          value={
            <span
              className={
                result.unclassified > 0
                  ? 'numeric tabular-nums text-warning-text'
                  : 'numeric tabular-nums'
              }
            >
              {result.unclassified}
            </span>
          }
        />
        <Figure
          label="Allowance required"
          value={
            <MoneyDisplay
              value={result.provisionRequiredMinor}
              currency={result.currencyCode}
              className="text-left"
              emphasis="strong"
            />
          }
        />
        <Figure
          label="Movement posted"
          value={
            <MoneyDisplay
              value={result.provisionMovementMinor}
              currency={result.currencyCode}
              className="text-left"
              emphasis="strong"
              signed
              negativeStyle="parentheses"
            />
          }
        />
      </div>

      {result.journalEntry === null ? (
        <Alert tone="info">
          <CheckCircle2 aria-hidden />
          <div className="space-y-1">
            <AlertTitle>The run completed and posted nothing</AlertTitle>
            <AlertDescription>
              The allowance the book should carry already matches what it carries, so there was no
              movement to post. No journal entry was created — that is the ordinary answer to a
              second run on an unchanged book, not a failure.
            </AlertDescription>
          </div>
        </Alert>
      ) : (
        <Alert tone="success">
          <CheckCircle2 aria-hidden />
          <div className="space-y-1">
            <AlertTitle>
              Posted as journal <span className="font-mono">{result.journalEntry}</span>
            </AlertTitle>
            <AlertDescription>
              The movement is in the general ledger. Paste the entry number into the reference filter
              on the journals screen to read the entry and every line it posted; the audit record
              below is who ran it.
              {canReadAudit ? (
                <>
                  {' '}
                  <Button variant="link" size="sm" className="px-0" onClick={onToggleJournal}>
                    <FileSearch />
                    {showJournal ? 'Hide the audit record' : 'Show the audit record'}
                  </Button>
                </>
              ) : null}
            </AlertDescription>
          </div>
        </Alert>
      )}

      {showJournal && result.journalEntry !== null ? (
        <LedgerAuditTrail
          entityType="JournalEntry"
          entityId={result.journalEntry}
          canRead={canReadAudit}
          emptyHint="The posting is recorded under its entry number the moment it is written."
        />
      ) : null}
    </div>
  )
}

function Figure({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-heading">{value}</p>
    </div>
  )
}
